import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOrganization, useOrganizationList } from '@clerk/clerk-react';
import { cgToast } from 'modules/Common/ui/toast';
import { performOrgSwitch } from 'modules/App/orgSwitchStore';
import useConsultantOverviewStore from 'modules/Consultant/stores/consultantOverviewStore';

/**
 * Returns `openClient(client)`: switch the active Clerk org into a consultant's
 * client and land on its installations page. Shared by the client list's row
 * click and Enter so both remember the consultant org for the sidebar's back
 * button and record the client in Recently opened.
 */
export default function useOpenClient() {
  const history = useHistory();
  const { t } = useTranslation('overview');
  const { setActive } = useOrganizationList();
  const { organization: consultantOrg } = useOrganization();
  const setConsultantOrgId = useConsultantOverviewStore((s) => s.setConsultantOrgId);
  const recordRecentClient = useConsultantOverviewStore((s) => s.recordRecentClient);

  // Memoised: handed to every row and to the sidebar's recent-client buttons.
  return useCallback(
    /** @param {{id: string, organizationId?: string}} client  a ConsultantClient */
    async (client) => {
      if (!client.organizationId) {
        cgToast.warning(t('consultant.orgNotFoundTitle', 'Organization Not Found'), {
          description: t(
            'consultant.orgNotFoundBody',
            'This client does not have an associated organization.',
          ),
        });
        return;
      }
      // Store consultant's original organization ID before switching
      if (consultantOrg) {
        setConsultantOrgId(consultantOrg.id);
      }
      // performOrgSwitch owns the loader, the already-active guard and the
      // manual-nav claim (so OrgBoundary doesn't redirect to the overview — we
      // go to /operator-installations explicitly). OrgBoundary clears the query
      // cache, refreshes the backend token and remounts the app tree when the
      // active org changes — no reload needed.
      const switched = await performOrgSwitch({
        setActive,
        orgId: client.organizationId,
        activeOrgId: consultantOrg?.id,
        // navigateTo must match the navigate thunk's push target: OrgBoundary
        // holds the switch loader up until the router reaches it, so the
        // consultant overview never renders against the client operator org
        // mid-switch (the old-route-vs-new-org 404 flash).
        navigateTo: '/operator-installations',
        navigate: () => history.push('/operator-installations'),
      });
      if (switched) {
        // Only a client that actually opened counts as recent. The store is
        // module-level, so this write survives OrgBoundary's remount.
        recordRecentClient(client.id);
      } else {
        cgToast.error(t('consultant.switchFailedTitle', 'Failed to Switch Client'), {
          description: t(
            'consultant.switchFailedBody',
            'Could not switch to the client workspace. Please try again.',
          ),
        });
      }
    },
    [history, t, setActive, consultantOrg, setConsultantOrgId, recordRecentClient],
  );
}
